import React, { useState } from 'react';
import Icon from './icons/Icon';
import { useTenantAccess } from '../context/TenantAccessContext';
import { useSelectedOrganization } from '../context/OrganizationContext';
import './OrganizationSelector.css';

/**
 * Authorization organization/tenant selector (populates x-mia-acl-context).
 *
 * This is the authz scope selector — DISTINCT from the Console company selector
 * (workspace-selector) above it. Picking an organization here updates
 * selectedOrganization (localStorage `app-selected-organization`), which every
 * authenticated request stamps as `x-mia-acl-context` via the fetchRegistry wrapper.
 * Mirrors ai-foundry-website's organization switcher.
 */
export default function OrganizationSelector() {
  const { tenants, loading, accessByOrganization } = useTenantAccess();
  const { selectedOrganization, setSelectedOrganization } = useSelectedOrganization();
  const [isOpen, setIsOpen] = useState(false);

  // Nothing to select (authz not set up / no tenants) → render nothing rather than an empty control.
  if (!loading && (!tenants || tenants.length === 0)) {
    return null;
  }

  const current = tenants.find((t) => t.id === selectedOrganization?.tenant);
  const label = loading
    ? 'Loading…'
    : (current?.title || selectedOrganization?.tenantName || 'Select organization');

  return (
    <div className="org-selector">
      <div className="org-selector-caption">Authorization scope</div>
      <button
        className="org-button"
        onClick={() => setIsOpen((o) => !o)}
        title="Organization used for authorization (x-mia-acl-context)"
      >
        <div className="org-info">
          <div className="org-name">{label}</div>
          {current?.organization && (
            <div className="org-sub">{current.organization}</div>
          )}
        </div>
        <Icon name="chevron-down" size={16} />
      </button>

      {isOpen && tenants.length > 0 && (
        <>
          <div className="dropdown-overlay" onClick={() => setIsOpen(false)} />
          <div className="org-dropdown">
            {tenants.map((tenant) => {
              const hasAccess = accessByOrganization?.[tenant.organization];
              const active = selectedOrganization?.tenant === tenant.id;
              return (
                <button
                  key={tenant.id}
                  className={`org-option ${active ? 'active' : ''} ${hasAccess ? '' : 'no-access'}`}
                  onClick={() => {
                    setSelectedOrganization({
                      organization: tenant.organization,
                      tenant: tenant.id,
                      tenantName: tenant.title,
                    });
                    setIsOpen(false);
                  }}
                >
                  <div className="org-option-info">
                    <div className="org-option-name">{tenant.title || tenant.id}</div>
                    <div className="org-option-sub">
                      {tenant.organization}{hasAccess ? '' : ' · no access'}
                    </div>
                  </div>
                  {active && <Icon name="check-circle" size={16} className="org-check" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
